import { Component, OnInit, ViewChild, ElementRef, HostListener, AfterViewChecked, Inject, OnDestroy,} from '@angular/core';
import { BehaviorSubject, combineLatest, map, mergeMap, Subject, take, takeUntil, throwError, timer } from 'rxjs';
import { Router, RouterOutlet, RouterModule, ActivatedRoute } from '@angular/router';
import { CommonModule, Location, DOCUMENT } from '@angular/common';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { environment } from '../../environments/environment';
import { Amplify } from 'aws-amplify';

import { AuthService } from '../auth.service';
import { AppService } from '../app.service';
import { StoriesService } from '../stories.service';
import { StoryService } from '../create-story/story.service';
import { HomeService } from './home.service';
import { AmplifyService, GetContentsQuery, Product, Sex } from '../amplify.service';
import { EncountersService } from '../new-encounters/encounters.service';
import { LoaderService } from '../loader.service';
import { LayoutService } from '../layout.service';
import { CriteriaService } from '../criteria.service';
import { ChatService } from '../chat/chat.service';
import { UrlService } from '../url.service';

import { LoaderComponent } from '../shared/loader/loader.component';
import { LoggedInHeaderComponent } from '../logged-in-header/logged-in-header.component';
import { InfoDialogComponent } from '../dialog/info-dialog/info-dialog.component';
import { StoryComponent } from '../story/story.component';

import { User } from '../shared/interfaces/user';
import { Story } from '../shared/interfaces/story';
import { ErrorReturn } from '../shared/interfaces/errorReturn';

import { InitialStoryPhotoArray, PhotoObject } from '../create-story/images-holder/story-photos';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    RouterOutlet,
    LoaderComponent,
    LoggedInHeaderComponent,
    RouterModule,
    InfoDialogComponent,
    StoryComponent,
    CommonModule,
    MatSidenavModule,
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, AfterViewChecked, OnDestroy {
  @HostListener('window:scroll', ['$event']) private onScrollWindow() {
    this.windowScrolling();
  }

  @ViewChild('mainHolder') holder!: ElementRef;
  @ViewChild('fileUpload') fileUpload!: ElementRef;
  @ViewChild('mobileFileUpload') mobileFileUpload!: ElementRef;  
  @ViewChild('sidenav') sidenav!: MatSidenav
  @ViewChild('photos') photosContainer!: ElementRef
  @ViewChild('scrollable') scrollable!: ElementRef

  loading$ = this.loaderService.loading$
  private _window: (Window & typeof globalThis) | null
  firstname!: string;
  user: User | null = null;
  photo!: string;
  savedImageUrl = '';
  stories: Story[] = [];
  storyImages: PhotoObject[] = [];
  showOverlay = false;
  showInfoDialog = false;
  currentPage = 0;
  totalPages = 1
  encounters: User[] = [];
  infoDialogType = '';
  serviceName = environment.serviceName;
  pageDisplayed = 'home';
  loadingMore = false
  noMoreToLoad = false
  sidenavToggled = false
  storyWidth!: number
  count = 0
  photos = [...InitialStoryPhotoArray]
  newStoriesAvailable = new BehaviorSubject(false)
  newEncountersAvailable = false
  dialogDisplayed = false
  private readonly _destroyed = new Subject<void>()
  isDesktop = this.layoutService.isDesktop
  liveNotifications$ = this.homeService.liveNotifications
  notificationsCount$ = new BehaviorSubject<number | null>(null)
  storiesFromGetContentsQuery!: GetContentsQuery[]
  storyMessage: {
    origin: 'edit' | 'create' | null,
    type: 'posted' | 'onHold' | null
  } = {
    origin: null,
    type: null
  }

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private appService: AppService,
    private authService: AuthService,
    private storiesService: StoriesService,
    private storyService: StoryService,
    private homeService: HomeService,
    private location: Location,
    private amplifyService: AmplifyService,
    private encountersService: EncountersService,
    private layoutService: LayoutService,
    private loaderService: LoaderService,
    private criteriaService: CriteriaService,
    private chatService: ChatService,
    private urlService: UrlService, // enables url history storage
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.appService.setPage('service');

    // management of navigation with browser arrows
    this._window = this.document.defaultView

    if(this._window){
      this._window.addEventListener('popstate', (event) => {
        const popStateTarget = event.currentTarget as Window
        const urlSegmentsArr = popStateTarget.location.pathname.split('/')

        switch (popStateTarget.location.pathname) {
          case '/home':
            this.homeService.toggleSidenav.next(false)
            this.homeService.pageDisplayed.next('home')
            break;

          case '/login':
            this.homeService.toggleSidenav.next(false)
            this.homeService.pageDisplayed.next('home')
            break;

          default:
            if(popStateTarget.location.pathname.includes('story')){
              if(!popStateTarget.location.pathname.includes('profile')){
                this.homeService.pageDisplayed.next('story')
              }
            } else if (popStateTarget.location.pathname.includes('my-matches')){
              this.homeService.pageDisplayed.next('my-matches')
            } else {
              this.homeService.pageDisplayed.next(urlSegmentsArr[urlSegmentsArr.length-1])
            }
            break;
        }
      }, false);
    }
  }

  async ngOnInit() {
    this.loaderService.show()

    if(this.router.url === '/home') this.pageDisplayed = 'home'
    if(this.pageDisplayed === 'home') { 
      this.location.replaceState('/home')
      this.homeService.pageDisplayed.next('home')
    }

    this.homeService.noMoreEncountersAvailable.pipe(takeUntil(this._destroyed)).subscribe(res => this.newEncountersAvailable = !res)
    this.homeService.pageDisplayed.pipe(takeUntil(this._destroyed)).subscribe(res => this.pageDisplayed = res)
    this.homeService.dialogDisplayed.pipe(takeUntil(this._destroyed)).subscribe(res => this.dialogDisplayed = res)
    this.storiesService.storyMessage.pipe(takeUntil(this._destroyed)).subscribe(res => {
      this.storyMessage = res
    
      if(this.storyMessage.origin === 'create'){
        setTimeout(() => {
          this.storyMessage = {origin: null, type: null}
          this.storiesService.storyMessage.next({origin: null, type: null})
        }, 3000)
      }
    })

    this.homeService.toggleSidenav.pipe(takeUntil(this._destroyed)).subscribe(res => {
      this.sidenavToggled = res
      if(this.sidenav) {
        if(this.sidenavToggled) this.sidenav.open()
        else this.sidenav.close()
      }
    })

    if(this.router.url.endsWith('home')) this.homeService.toggleSidenav.next(false)

    this.user = JSON.parse(this.authService.get('user'))
    const userStoredLocally = !!this.user
    let id: number | undefined

    if (userStoredLocally) {
      id = this.user?.id 

      if(!this.authService.authMe$.value){
        if (id) {
          this.authService.getProfile(id).pipe(takeUntil(this._destroyed)).subscribe({
            next: (res: User | null) => {
              this.user = res
  
              this.authService.authMe$.next(this.user)
  
              this.authService.remove('user')
              this.authService.set('user', JSON.stringify(res))
  
              if(this.user?.main_photo){
                (this.photo = this.user?.main_photo.url)
  
                this.stories.splice(0,0,{
                  id: 0,
                  photos: [{
                    url: this.user.main_photo.url
                  }]
                })
              } else {
                this.router.navigate(['/upload-photo'])
              }
            },
            error: (err) => {
              return throwError(() => err)
            },
          });
        }
      } else {
        this.user = this.authService.authMe$.value
        if(this.user?.main_photo){
          (this.photo = this.user?.main_photo.url)

          this.stories.splice(0,0,{
            id: 0,
            photos: [{
              url: this.user.main_photo.url
            }]
          })
        } else {
          this.router.navigate(['/upload-photo'])
        } 
      }
    }

    Amplify.configure({
      API: {
        GraphQL: {
          endpoint: 'https://ynzsakwquzd7zkuzzem4fk5644.appsync-api.eu-west-1.amazonaws.com/graphql',
          region: 'eu-west-1',
          defaultAuthMode: 'apiKey',
          apiKey: 'da2-dsmvlevcxzcopbleelbamymvkm'
        }
      }
    });

    this.getStories(this.currentPage, 9).then((res) => {
      res.map(s => {
        const story = this._transformStoryFromGetContentsToStory(s)
        this.stories.push(story)
      })

      this.loaderService.hide()
    })    

    this.amplifyService.AddedContentListener().subscribe({
      next: ({ data }) => {
        console.log(data)
        this.criteriaService.getCriteria().pipe(takeUntil(this._destroyed)).subscribe((criteria) => {
          // if a story was added, we compare its owner to the user's dating criteria (sex and age) before updating the stories array and showing the notification
          if(data.addedContent?.item?.data?.type === "story" && data.addedContent?.item?.data?.user){
            const newStoryOwner = data.addedContent.item?.data.user

            if(!newStoryOwner.age) return

            if(newStoryOwner.sex === criteria.sex && (newStoryOwner.age <= criteria.age_end && newStoryOwner.age >= criteria.age_start)){
              this.newStoriesAvailable.next(true)
              const addedContent = data.addedContent.item?.data

              if(!addedContent) return
              const newStory: Story = {
                id: addedContent.content_id as number,
                views: addedContent.views as number,
                likes: addedContent.likes as number,
                type: addedContent.type as string,
                photos: this._transformStoryFromGetPhotos(addedContent.photos),
                user: addedContent.user as User,
              }

              this.stories = [...this.stories, newStory]

              setTimeout(() => {
                this.newStoriesAvailable.next(false)
              }, 5000)
            }
          }
        })
      },
      error: (error) => console.warn(error)
    });

    this.amplifyService.UpdatedContentListener().subscribe((updated) => {
      console.log(updated)

      let found!: Story | undefined
      console.log(this.storiesService.flaggedStoryId)
      const updatedStoryId = updated.data.updatedContent.item?.content_id
      console.log(updatedStoryId)
      if(this.storiesService.flaggedStoryId && this.storiesService.flaggedStoryId === updatedStoryId){
        //item.document
        found = this.stories.find(
          (s) => s.id === updatedStoryId
        )
      } else if(this.storiesService.blockedUserId === updated.data.updatedContent.item?.document?.user?.id){
        //item.document
        found = this.stories.find(
          (s) => s.user?.id === updated.data.updatedContent.item?.document?.user?.id
        )
      } else if(!this.storiesService.flaggedStoryId || this.storiesService.flaggedStoryId !== updatedStoryId || 
        this.storiesService.blockedUserId || this.storiesService.blockedUserId !== updated.data.updatedContent.item?.document?.user?.id) {
        //item.document
        found = this.stories.find(
          (s) => s.id === updatedStoryId
        )
      }

      switch(true){
        // removal of flagged story (not validated yet through the BO)
        case (found && (this.storiesService.flaggedStoryId && this.storiesService.flaggedStoryId === updatedStoryId)):
          {
            found.reported = true
            const index = this.stories.indexOf(found)
            this.stories.splice(index, 1, found)
          }
        break;
        // remove all stories from the blocked user
        case (found && (this.storiesService.blockedUserId === updated.data.updatedContent.item?.document?.user?.id)):
          {
            this.stories.forEach(s => {
              if(s.user?.id === updated.data.updatedContent.item?.document?.user?.id){
                s.reported = true
              }
            })
          }
        break;
        // removal of flagged story (validated through the BO)
        case (found && (found.user?.id === updated.data.updatedContent.item?.document?.reported)):
          {
            found.reported = !!updated.data.updatedContent.item?.document?.reported
            const index = this.stories.indexOf(found)
            this.stories.splice(index, 1)
          }
        break;
        // update of story likes number
        case (found && (updated.data.updatedContent.item?.document?.likes !== null) || 
          (updated.data.updatedContent.item?.document?.likes !== undefined && 
          found?.likes !== null && found?.likes !== undefined)):
          {
            const foundUpdated = found
            if(foundUpdated.likes !== null && foundUpdated.likes !== undefined){
              foundUpdated.likes = updated.data.updatedContent.item?.document?.likes
              const index = this.stories.indexOf(foundUpdated)
              this.stories.splice(index, 1, foundUpdated)
            }
          }
        break;
        // update of story views number
        case (found && updated.data.updatedContent.item?.document?.views && (found.views !== null) && (found.views !== undefined && !found.already_seen)):
          {
            const foundUpdated = found
            if(foundUpdated.views !== null && foundUpdated.views !== undefined){
              foundUpdated.views = updated.data.updatedContent.item.document.views
              const index = this.stories.indexOf(foundUpdated)
              this.stories.splice(index, 1, foundUpdated)  
            }
          }
        break;

        default: return
      }


      // if(found){
      //   // removal of flagged story (not validated yet through the BO)
      //   if(this.storiesService.flaggedStoryId && this.storiesService.flaggedStoryId === updatedStoryId){
      //     found.reported = true
      //     const index = this.stories.indexOf(found)
      //     this.stories.splice(index, 1, found)
      //   } else if(this.storiesService.blockedUserId === updated.data.updatedContent.item?.document?.user?.id){
      //     // remove all stories from the blocked user
      //     this.stories.forEach(s => {
      //       if(s.user?.id === updated.data.updatedContent.item?.document?.user?.id){
      //         s.reported = true
      //       }
      //     })
      //   } else if (found.user?.id === updated.data.updatedContent.item?.document?.reported){
      //     // removal of flagged story (validated through the BO)
      //     found.reported = !!updated.data.updatedContent.item?.document?.reported
      //     const index = this.stories.indexOf(found)
      //     this.stories.splice(index, 1)
      //   } else if(updated.data.updatedContent.item?.document?.likes !== null || 
      //     updated.data.updatedContent.item?.document?.likes !== undefined && 
      //     found.likes !== null && found.likes !== undefined){
      //     // update of story likes number
      //     const foundUpdated = found
      //     if(foundUpdated.likes !== null && foundUpdated.likes !== undefined){
      //       foundUpdated.likes = updated.data.updatedContent.item?.document?.likes
      //       const index = this.stories.indexOf(foundUpdated)
      //       this.stories.splice(index, 1, foundUpdated)
      //     }
      //   } else if(updated.data.updatedContent.item?.document?.views && found.views !== null && found.views !== undefined && !found.already_seen){
      //     //update of story views number
      //     const foundUpdated = found
      //     if(foundUpdated.views !== null && foundUpdated.views !== undefined){
      //       foundUpdated.views = updated.data.updatedContent.item.document.views
      //       const index = this.stories.indexOf(foundUpdated)
      //       this.stories.splice(index, 1, foundUpdated)  
      //     }
  
      //   }
      // }
    })

    this.amplifyService.RemovedContentListener().subscribe((removed) => {
      const found = this.stories.find(s => s.id === removed.data.removedContent.content_id)

      if(found){
        const index = this.stories.indexOf(found)
        this.stories.splice(index,1)
      }
    })

    // suggestions and invitations for the new encounters section
    timer(0, 300000).pipe(
      takeUntil(this._destroyed),
      mergeMap(() => {
        return combineLatest([this.encountersService.getSuggestions(1,30), this.authService.getAuthContactsRequest(1)]).pipe(
          map(([suggestions, invitations]) => {
            const encounters = [...invitations, ...suggestions]
            this.encounters = suggestions
            this.homeService.encounters.next(encounters)

            this.newEncountersAvailable = encounters.length > 0
          }),
        )
      }),
    ).subscribe()

    timer(500, 20000).pipe(
      takeUntil(this._destroyed),
      mergeMap(() => {
        return combineLatest([this.chatService.getMessagesCount(), this.homeService.getNotificationsCount()]).pipe(
          map(([messagesCount, notificationsByType]) => {
            let count = 0
            
            for(const [, value] of Object.entries(notificationsByType)){
              count += value
            }

            count += messagesCount

            this.notificationsCount$.next(count)
            this.homeService.messagesCount$.next(messagesCount)
            this.homeService.liveNotifications$.next(count)
          }),
        )
      }),
    ).subscribe()

    this.storiesService.storyEditionClosed.pipe(take(1)).subscribe(closed => {
      if(closed){
        this.router.navigate(['/home/my-space/my-stories'])
        this.homeService.pageDisplayed.next('my-stories')
      }
    })

    this.route.queryParams.subscribe((param) => {
      const customerServicePhone = '+33970340348'
      const customerServiceEmail = 'customer&#64;swipi.com'
      if (param['failure']) {
        const err: ErrorReturn = {
          code: 404,
          error_code: 404,
          level: 'subscribe notfound',
          message:
            $localize `:Text for warning dialog if the subscription fails@@ERROR_DURING_SUB:An error occurred during your subscription. Please try again later or contact Customer Services by telephone on ${customerServicePhone} (price of a local call) or by email on ${customerServiceEmail}.`
        };
        this.appService.setPopin(err);
      }

      if (param['user']) {
        const err: ErrorReturn = {
          code: 200,
          error_code: 200,
          level: 'subscribe already exist',
          message: '',
        };
        this.appService.setPopin(err);
      }
    });
  }

  private _transformStoryFromGetContentsToStory(storyFromGetContents: GetContentsQuery){
    return {
        id: storyFromGetContents.item?.data?.content_id,
        user: storyFromGetContents.item?.data?.user as User,
        likes: storyFromGetContents.item?.data?.likes as number,
        photos: this._transformStoryFromGetPhotos(storyFromGetContents.item?.data?.photos) || [],
        views: storyFromGetContents.item?.data?.views as number,
        already_seen: storyFromGetContents.item?.data?.already_seen?.includes(this.user!.id)
    }
  }

  private _transformStoryFromGetPhotos(photos: (string | null)[] | null | undefined): {id?: number, url: string | null}[] | []{
    if(!photos) return []
    const storyPhotos = photos.map(p => {
      return {url: p}
    })
    return storyPhotos
  }

  seeNewStories(){
    this._window?.scrollTo(0,0)
    this.reloadComponent(true)
  }

  reloadComponent(self: boolean, urlToNavigateTo?: string){
    //skipLocationChange:true means dont update the url to / when navigating
    const url = self ? this.router.url : urlToNavigateTo

    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([`/${url}`])
    })
 }

  onScroll(e: Event){
    // get scroll value of the #scrollable div in the home to store it in a service
    this.appService.scrollValue.next(e)
  }

  onTouchEnd(e: TouchEvent){
    this.appService.scrollValue.next(e)
  }

  ngAfterViewChecked(){
    if(this.count > 0) return

    setTimeout(() => {
      const bounds = this.photosContainer.nativeElement.getBoundingClientRect()
      const photosContainerWidth = Number.parseFloat((bounds.right - bounds.left).toFixed(2))
  
      const gapWidth = 20
      let numberOfItemsPerRow = 2
  
      if(this.isDesktop()) numberOfItemsPerRow = 5
  
      if(!this.storyWidth) this.storyWidth = Math.floor((photosContainerWidth - (numberOfItemsPerRow - 1) * gapWidth) / numberOfItemsPerRow)
      
      this.count++

      //open the drawer once the sidenav exists in the DOM (use case example: refresh on edit story)
      if(this.sidenav) {
        if(this.sidenavToggled) this.sidenav.open()
        else this.sidenav.close()
      }
    }, 100)
  }

  getStyles(){
    return {
      'width': this.storyWidth + 'px',
      'height': `${this.storyWidth * 1.4}px`,
    }
  }

  toggleSidenav(open: boolean){
    this.homeService.toggleSidenav.next(open)
    if(!open) this.homeService.pageDisplayed.next('')
  }

  windowScrolling() {
    const scrollValue = window.scrollY;
    const content = this.holder.nativeElement as HTMLElement;
    const bottom = scrollValue > content.clientHeight - window.innerHeight - 200;

    if(bottom && !this.loadingMore && !this.noMoreToLoad) {
      this.loadingMore = true
      this.currentPage++

      this.getStories(this.currentPage === 1 ? this.currentPage * 9 : this.currentPage * 10, 10).then((res) => {
        res.map(s => {
          const story = this._transformStoryFromGetContentsToStory(s)
          this.stories.push(story)
        })

        if(res.length < 10) this.noMoreToLoad = true

        this.loaderService.hide()
        if(!this.loadingMore) {
          window.scrollTo(0, 0)
        }
        this.loadingMore = false
        this.loaderService.hide()
      })      
    }
  }

  getShowStoryRequest(story: Story){
    if(!story) return
    //this.storiesService.story.next(story)
    this.router.navigate(['home/story/' + story.id])
    this.homeService.pageDisplayed.next('story')
    this.homeService.toggleSidenav.next(true)
    this.storiesService.storyOwnedByMe = false
  }

  async getStories(start: number, offset: number) {
    console.log('getStories')
    this.loaderService.show()

    return this.storiesFromGetContentsQuery = await this.amplifyService.GetContents(
      Product.weezchat_fr, 
      18, 
      99, 
      -1, 
      undefined, 
      undefined, 
      this.user?.attraction === "woman" ? Sex.woman : Sex.man, 
      "[\"story\"]", 
      start, 
      offset,
      undefined, 
      this.user?.id, 
      1
    ) //false 
  }

  // add image from the explorer
  onPhotoOrVideoInputChange(event: Event) {
    console.log('photo selection')
    this.photos = [
      {
          src: null,
          thumbnailSrc: null,
          saved: false,
          edited: false
      },
      {
          src: null,
          thumbnailSrc: null,
          disabled: false,
          saved: false,
          edited: false
      },
      {
          src: null,
          thumbnailSrc: null,
          disabled: true,
          saved: false,
          edited: false
      },
      {
          src: null,
          thumbnailSrc: null,
          disabled: true,
          saved: false,
          edited: false
      },
      {
          src: null,
          thumbnailSrc: null,
          disabled: true,
          saved: false,
          edited: false
      }
    ]

    const element = event.currentTarget as HTMLInputElement;
    const files = element.files;

    Array.prototype.forEach.call(files, (file: File) => {
      const fileBlob = URL.createObjectURL(file);

      this.photos[0].src = fileBlob
      this.storyService.storyImages.next(this.photos)
    });

    this.router.navigate(['create-story']);
  }

  displayAddStoryDialog() {
    console.log('displayAddStoryDialog')

    this.router.navigate(['story'])
    this.homeService.toggleSidenav.next(true)
  }

  openMobileCamera() {
    this.mobileFileUpload.nativeElement.click();
  }

  openFileInput() {
    this.fileUpload.nativeElement.click();
  }

  drop(ev: DragEvent) {
    if (!ev.dataTransfer) return;
    //stops browser from opening the file
    ev.preventDefault();

    if (ev.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      for (const item of ev.dataTransfer.items) {
        if (item.kind !== 'file') {
          return;
        }

        const file = item.getAsFile();

        if (!file) return;
        const fileBlob = URL.createObjectURL(file);

        this.storyService.storyImages.next([
          {
            id: this.storyImages.length,
            src: fileBlob,
            saved: false,
          },
        ]);
      }
    } else {
      // Use DataTransfer interface to access the file(s)
      for (const file of ev.dataTransfer.files) {
        const fileBlob = URL.createObjectURL(file);
        this.storyService.storyImages.next([
          {
            id: this.storyImages.length,
            src: fileBlob,
            saved: false,
          },
        ]);
      }
    }
  }

  allowDrop(ev: DragEvent) {
    ev.preventDefault();
  }

  closeInfoDialog(event: boolean) {
    this.showInfoDialog = !event;
  }

  requestStories($event: 'previous' | 'next') {
    if (!$event) return;

    if (this.currentPage === 1 && $event === 'previous') return;

    this.getStories(this.currentPage === 1 ? this.currentPage * 9 : this.currentPage * 10, 10).then((res) => {
      res.map(s => {
        const story = this._transformStoryFromGetContentsToStory(s)
        this.stories.push(story)
      })

      if(res.length < 10) this.noMoreToLoad = true

      this.loaderService.hide()
      if(!this.loadingMore) {
        window.scrollTo(0, 0)
      }
      this.loadingMore = false
      this.loaderService.hide()
    })
  }

  getInfoDialogType(event: string) {
    this.infoDialogType = event;
    this.showInfoDialog = true;
  }

  ngOnDestroy(){
    this._destroyed.next()
    this._destroyed.complete()
    this.homeService.stop()
  }
}

